import SocketKeepAlive from './socketKeepAlive'

const setupSocketListeners = Symbol('setupSocketListeners')

/**
 * This behaves like a WebSocket in every way, except if it fails to connect,
 * or it gets disconnected, it will repeatedly poll until it successfully connects
 * again.
 */
class Socket {
    // --Public variables--

    /** The number of attempted reconnects since starting, or the last successful connection.*/
    reconnectAttempts = 0

    /**
     * The current state of the connection.
     * Can be one of: WebSocket.CONNECTING, WebSocket.OPEN, WebSocket.CLOSING, WebSocket.CLOSED
     */
    readyState = WebSocket.CONNECTING

    /**
     * A string indicating the name of the sub-protocol the server selected; this will be one of
     * the strings specified in the protocols parameter when creating the WebSocket object.
     */

    protocol = null

    // --Private variables--

    #ws
    #forcedClose = false
    #timedOut = false
    #eventTarget = document.createElement('div')
    #heartBeat = () => {}
    #queue = []

    // --Static variables--

    /**
     * Whether all instances of Socket should log debug messages.
     * Setting this to true is the equivalent of setting all instances of Socket.debug to true.
     */

    static debugAll = false

    static CONNECTING = WebSocket.CONNECTING

    static OPEN = WebSocket.OPEN

    static CLOSING = WebSocket.CLOSING

    static CLOSED = WebSocket.CLOSED

    static CONNECTIONS = {}

    /**
     * Parameters
     * ==========
     * url - The url you are connecting to.
     * protocols - Optional string or array of protocols.
     * options can either be passed upon instantiation or set after instantiation.
     */
    constructor(url, protocols, options = {}) {
        const defaults = {
            /** Whether this instance should log debug messages. */
            debug: false,

            /** Whether or not the websocket should attempt to connect immediately upon instantiation. */
            automaticOpen: true,

            /** The number of milliseconds to delay before attempting to reconnect. */
            reconnectInterval: 1000,
            /** The maximum number of milliseconds to delay a reconnection attempt. */
            maxReconnectInterval: 30000,
            /** The rate of increase of the reconnect delay. Allows reconnect attempts to back off when problems persist. */
            reconnectDecay: 1.5,

            /** The maximum time in milliseconds to wait for a connection to succeed before closing and retrying. */
            timeoutInterval: 2000,

            /** The maximum number of reconnection attempts to make. Unlimited if null. */
            maxReconnectAttempts: null,

            /** The binary type, possible values 'blob' or 'arraybuffer', default 'blob'. */
            binaryType: 'blob',

            /** In the event that the user is idle it'll attempt to send a data packet. */
            keepAlive: true,

            keepAliveIntervalTime: 60 * 1000 * 5, // 5 mins
        }

        // Overwrite and define settings with options if they exist.
        for (var key in defaults) {
            if (typeof options[key] !== 'undefined') {
                this[key] = options[key]
            } else {
                this[key] = defaults[key]
            }
        }

        this.url = url
        this.protocols = protocols

        // Expose event listener API
        this.addEventListener = this.#eventTarget.addEventListener.bind(this.#eventTarget)
        this.removeEventListener = this.#eventTarget.removeEventListener.bind(this.#eventTarget)
        this.dispatchEvent = this.#eventTarget.dispatchEvent.bind(this.#eventTarget)

        const self = this

        this.addEventListener('open', event => self.onopen && self.onopen(event))
        this.addEventListener('close', event => self.onclose && self.onclose(event))
        this.addEventListener('connecting', event => self.onconnecting && self.onconnecting(event))
        this.addEventListener('message', event => self.onmessage && self.onmessage(event))
        this.addEventListener('error', event => self.onerror && self.onerror(event))

        if (this.keepAlive) {
            const keepAlive = new SocketKeepAlive({
                keepAliveCallback: _ => {
                    const ping = JSON.stringify({ action: 'ping' })

                    if (self.#ws) self.#ws.send(ping)
                },
                intervalTime: this.keepAliveIntervalTime,
            })

            this.#heartBeat = keepAlive.heartBeat
        }

        // Whether or not to create a websocket upon instantiation
        if (this.automaticOpen == true) this.open(false)
    }

    open(reconnectAttempt = false) {
        if (Socket.CONNECTIONS[this.url] && Socket.CONNECTIONS[this.url].readyState == Socket.OPEN) {
            this.#ws = Socket.CONNECTIONS[this.url]

            this[setupSocketListeners](reconnectAttempt, true)
        } else {
            this.#ws = new WebSocket(this.url, this.protocols || [])
            this.#ws.binaryType = this.binaryType
            Socket.CONNECTIONS[this.url] = this.#ws

            this[setupSocketListeners](reconnectAttempt, false)
        }

        if (reconnectAttempt) {
            if (this.maxReconnectAttempts && this.reconnectAttempts > this.maxReconnectAttempts) return
        } else {
            this.#eventTarget.dispatchEvent(generateEvent('connecting'))
            this.reconnectAttempts = 0
        }

        if (this.debug || Socket.debugAll) console.debug('Socket', 'attempt-connect', this.url)
    }

    /**
     * Transmits data to the server over the WebSocket connection.
     *
     * @param data a text string, ArrayBuffer or Blob to send to the server.
     */
    send(data) {
        if (this.#ws) {
            if (this.debug || Socket.debugAll) console.debug('Socket', 'send', this.url, data)

            this.#heartBeat()

            if (this.#ws.readyState !== this.#ws.OPEN) {
                this.addQueue(data)
                return
            }

            return this.#ws.send(data)
        } else {
            this.addQueue(data)
        }
    }

    sendMessage(action, message) {
        this.send(
            JSON.stringify({
                action,
                data: message,
            })
        )
    }

    /**
     * Closes the WebSocket connection or connection attempt, if any.
     * If the connection is already CLOSED, this method does nothing.
     */
    close(code = 1000, reason) {
        this.#forcedClose = true

        if (this.#ws) this.#ws.close(code, reason)
    }

    addQueue(message) {
        this.#queue.push(message)
    }

    isQueueEmpty() {
        return this.#queue.length === 0
    }

    flushQueue() {
        while (!this.isQueueEmpty()) {
            const message = this.#queue.shift()
            this.send(message)
        }
    }

    /**
     * Additional public API method to refresh the connection if still open (close, re-open).
     * For example, if the app suspects bad data / missed heart beats, it can try to refresh.
     */
    refresh() {
        if (this.#ws) this.#ws.close()
    }

    /**
     * Setup WebSocket callback functions and apply retry logic
     */
    [setupSocketListeners](reconnectAttempt, hasConnection) {
        const ws = this.#ws
        const self = this

        const intervalTimeout = !hasConnection
            ? setTimeout(function() {
                  if (self.debug || Socket.debugAll) {
                      console.debug('Socket', 'connection-timeout', self.url)
                  }
                  self.#timedOut = true
                  self.#ws.close()
                  self.#timedOut = false
              }, this.timeoutInterval)
            : null

        ws.onopen = function(event) {
            clearTimeout(intervalTimeout)

            if (self.debug || Socket.debugAll) console.debug('Socket', 'onopen', self.url)

            self.protocol = self.#ws.protocol
            self.readyState = WebSocket.OPEN
            self.reconnectAttempts = 0

            let e = generateEvent('open')
            e.isReconnect = reconnectAttempt
            reconnectAttempt = false

            self.#eventTarget.dispatchEvent(e)

            if (!self.isQueueEmpty()) {
                self.flushQueue()
            }
        }

        ws.onclose = function(event) {
            clearTimeout(intervalTimeout)
            self.#ws = null

            if (self.#forcedClose) {
                self.readyState = WebSocket.CLOSED
                self.#eventTarget.dispatchEvent(generateEvent('close'))
            } else {
                self.readyState = WebSocket.CONNECTING

                let e = generateEvent('connecting')
                e.code = event.code
                e.reason = event.reason
                e.wasClean = event.wasClean

                self.#eventTarget.dispatchEvent(e)

                if (!reconnectAttempt && !self.#timedOut) {
                    if (self.debug || Socket.debugAll) console.debug('Socket', 'onclose', self.url)

                    self.#eventTarget.dispatchEvent(generateEvent('close'))
                }

                const timeout = self.reconnectInterval * Math.pow(self.reconnectDecay, self.reconnectAttempts)

                setTimeout(
                    function() {
                        self.reconnectAttempts++
                        self.open(true)
                    },
                    timeout > self.maxReconnectInterval ? self.maxReconnectInterval : timeout
                )
            }
        }

        ws.onmessage = function(event) {
            if (self.debug || Socket.debugAll) console.debug('Socket', 'onmessage', self.url, event.data)

            let e = generateEvent('message')
            e.data = event.data

            self.#eventTarget.dispatchEvent(e)
        }

        ws.onerror = function(event) {
            if (self.debug || Socket.debugAll) console.debug('Socket', 'onerror', self.url, event)

            self.#eventTarget.dispatchEvent(generateEvent('error'))
        }
    }
}

/**
 * Create an event with the specified event name and arguments
 *
 * @param string eventName
 * @param string args
 */
const generateEvent = (eventName, args) => {
    let evt = document.createEvent('CustomEvent')
    evt.initCustomEvent(eventName, false, false, args)
    return evt
}

export default Socket
